// Multi-clause sort editor — an ordered list of {property, dir} rows (primary
// first) with add / remove / drag-reorder. The property is chosen with the
// SAME selector as filters (AspectCatalogMolecule + the shared property
// catalog), so sort and filter property selection are identical.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import AspectCatalogMolecule from '../AspectCatalog/AspectCatalogMolecule';
import { propertyOptionsToCatalogEntries } from '../../../utils/catalogBuilders';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { SortClause } from '../../../types/contextFlow';
import { nextFilterId, withStableIds } from '../../../utils/ids';
import './SortEditorMolecule.scss';

export interface SortEditorMoleculeProps {
  /** Ordered sort clauses (primary first). */
  value: SortClause[];
  /** The property catalog — the same options the filter editor uses. */
  availableProperties: PropertyOption[];
  onChange: (next: SortClause[]) => void;
  readOnly?: boolean;
  className?: string;
}

const SortEditorMolecule: React.FC<SortEditorMoleculeProps> = ({
  value,
  availableProperties,
  onChange,
  readOnly = false,
  className = '',
}) => {
  const { t } = useTranslation();
  // Index whose property picker is open (a fresh row opens its picker immediately).
  const [pickingIndex, setPickingIndex] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Normalise once per `value` identity change — never inside the render
  // `.map()` below, which would mint a fresh id (and key) every render.
  const rows = useMemo(() => withStableIds(value), [value]);

  // One catalog, one UX: the sort editor picks its property from the same rows
  // the filter editor offers, mapped from the list this surface already scoped.
  const sortCatalogEntries = useMemo(
    () => propertyOptionsToCatalogEntries(availableProperties, t),
    [availableProperties, t]
  );

  const labelFor = (key: string) =>
    availableProperties.find((p) => p.key === key)?.label ?? key;

  const update = (next: SortClause[]) => onChange(next);

  const addSort = () => {
    if (readOnly) return;
    update([...rows, { id: nextFilterId(), property: '', dir: 'asc' }]);
    setPickingIndex(rows.length);
  };

  const removeSort = (index: number) => {
    if (readOnly) return;
    update(rows.filter((_, i) => i !== index));
    setPickingIndex(null);
  };

  const setProperty = (index: number, property: string) => {
    update(rows.map((s, i) => (i === index ? { ...s, property } : s)));
    setPickingIndex(null);
  };

  const setDir = (index: number, dir: 'asc' | 'desc') => {
    update(rows.map((s, i) => (i === index ? { ...s, dir } : s)));
  };

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    update(next);
  };

  return (
    <div className={`sort-editor-molecule ${className}`.trim()} data-testid="sort-editor">
      <div className="sort-editor-molecule__header">
        <span className="sort-editor-molecule__eyebrow">{t('context.levelEditor.sort')}</span>
        {!readOnly && (
          <button
            type="button"
            className="sort-editor-molecule__add"
            onClick={addSort}
            data-testid="sort-editor-add"
          >
            <i className="pi pi-plus" aria-hidden="true" />
            {t('context.editor.inspector.level.sortAdd')}
          </button>
        )}
      </div>

      {rows.length === 0 && (
        <div className="sort-editor-molecule__empty" data-testid="sort-editor-empty">
          {t('context.editor.inspector.level.sortEmpty')}
        </div>
      )}

      <div className="sort-editor-molecule__rows">
        {rows.map((clause, index) => {
          if (pickingIndex === index) {
            return (
              <AspectCatalogMolecule
                key={clause.id}
                selectedKey={clause.property || null}
                entries={sortCatalogEntries}
                onPick={(entry) => setProperty(index, entry.key)}
                onCancel={() =>
                  clause.property ? setPickingIndex(null) : removeSort(index)
                }
                testIdPrefix="sort-editor-property"
                className="sort-editor-molecule__picker"
              />
            );
          }
          return (
            <div
              key={clause.id}
              className={`sort-editor-molecule__row${dragIndex === index ? ' sort-editor-molecule__row--dragging' : ''}`}
              data-testid={`sort-editor-row-${index}`}
              draggable={!readOnly && rows.length > 1}
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => {
                if (dragIndex !== null) e.preventDefault();
              }}
              onDrop={() => {
                if (dragIndex !== null) move(dragIndex, index);
                setDragIndex(null);
              }}
              onDragEnd={() => setDragIndex(null)}
            >
              {!readOnly && rows.length > 1 && (
                <span
                  className="sort-editor-molecule__handle"
                  aria-hidden="true"
                  data-testid={`sort-editor-handle-${index}`}
                >
                  <i className="pi pi-bars" />
                </span>
              )}
              <button
                type="button"
                className="sort-editor-molecule__property"
                onClick={() => !readOnly && setPickingIndex(index)}
                disabled={readOnly}
                data-testid={`sort-editor-property-summary-${index}`}
              >
                {clause.property
                  ? labelFor(clause.property)
                  : t('context.editor.inspector.level.sortPlaceholder')}
              </button>
              <button
                type="button"
                className="sort-editor-molecule__direction"
                onClick={() => !readOnly && setDir(index, clause.dir === 'asc' ? 'desc' : 'asc')}
                disabled={readOnly}
                aria-label={t('context.levelEditor.sort')}
                data-testid={`sort-editor-direction-${index}`}
                title={clause.dir === 'asc' ? t('context.editor.inspector.level.sortAsc') : t('context.editor.inspector.level.sortDesc')}
              >
                <i
                  className={`pi ${clause.dir === 'asc' ? 'pi-arrow-up' : 'pi-arrow-down'}`}
                  aria-hidden="true"
                />
                <span>{clause.dir === 'asc' ? t('context.editor.inspector.level.sortAsc') : t('context.editor.inspector.level.sortDesc')}</span>
              </button>
              {!readOnly && (
                <button
                  type="button"
                  className="sort-editor-molecule__remove"
                  aria-label={t('context.editor.inspector.level.sortRemove')}
                  onClick={() => removeSort(index)}
                  data-testid={`sort-editor-remove-${index}`}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

SortEditorMolecule.displayName = 'SortEditorMolecule';

export default SortEditorMolecule;
